// VNEC v9 — pages/ifrs.js
// IFRS Dashboard — sử dụng RPC get_ifrs_dashboard(p_year) từ 004_rpc.sql
// Data shape v9:
//   { year, doanh_thu, contract_direct, contract_allocated, obtain_contract,
//     operating_sga, gross_profit, gp_margin_pct, ebit, ebit_margin_pct }
// (Đơn giản hơn v8 — không có navy/gold/monthly breakdown từ RPC.)
// Inventory + Prepaid totals query trực tiếp ton_kho/prepaid_allocation.
import { supabase, getIFRSDashboard } from '../lib/supabase.js';
import { formatVND } from '../lib/forms.js';
import { fmtCompact, PALETTE, renderChart, destroyAllCharts } from '../lib/dashboard.js';

let _state = { selectedYear: new Date().getFullYear() };

export function renderIFRS() {
  window.__pageInit = initIFRS;
  const yr = new Date().getFullYear();
  const years = [yr, yr - 1, yr - 2];
  return `
    <div class="dash-wrap">
      <div class="page-hd">
        <h2>IFRS Dashboard</h2>
        <div class="dash-filter">
          <select id="yr-filter" class="inp">
            ${years.map(y => `<option value="${y}">${y}</option>`).join('')}
          </select>
          <button class="btn btn-ghost btn-sm" id="dash-refresh">⟳ Refresh</button>
          <span class="muted" id="dash-updated"></span>
        </div>
      </div>
      <div id="dash-content"><div class="loading">Đang tải dữ liệu…</div></div>
    </div>`;
}

async function initIFRS() {
  document.getElementById('dash-refresh').addEventListener('click', load);
  document.getElementById('yr-filter').addEventListener('change', e => {
    _state.selectedYear = parseInt(e.target.value); load();
  });
  await load();
}

async function load() {
  destroyAllCharts();
  const content = document.getElementById('dash-content');
  content.innerHTML = '<div class="loading">Đang tải…</div>';
  try {
    // 1) IFRS aggregated P&L
    const d = await getIFRSDashboard(_state.selectedYear);
    // 2) Gold figures: inventory + prepaid
    const [invRes, preRes] = await Promise.all([
      supabase.from('ton_kho').select('gia_tri_ton'),
      supabase.from('prepaid_allocation').select('gia_tri_pb, chi_phi_id')
    ]);
    const inventoryTotal = (invRes.data || []).reduce((s, r) => s + Number(r.gia_tri_ton || 0), 0);
    // Prepaid remaining: get approved Prepaid CPs' total − allocated
    const { data: prepaidCPs } = await supabase.from('chi_phi')
      .select('id, tong_tt').eq('phan_loai', 'Prepaid').eq('trang_thai', 'approved').is('deleted_at', null);
    const allocByCp = {};
    (preRes.data || []).forEach(r => { allocByCp[r.chi_phi_id] = (allocByCp[r.chi_phi_id] || 0) + Number(r.gia_tri_pb || 0); });
    const prepaidRemaining = (prepaidCPs || []).reduce((s, cp) => s + Math.max(0, Number(cp.tong_tt || 0) - (allocByCp[cp.id] || 0)), 0);

    document.getElementById('dash-updated').textContent = 'Cập nhật: ' + new Date().toLocaleString('vi-VN');
    renderDash(d, { inventoryTotal, prepaidRemaining });
  } catch (e) {
    content.innerHTML = `<div class="error-box">Lỗi: ${e.message}</div>`;
  }
}

function renderDash(d, gold) {
  const doanhThu = Number(d.doanh_thu || 0);
  const cDirect  = Number(d.contract_direct || 0);
  const cAlloc   = Number(d.contract_allocated || 0);
  const obtain   = Number(d.obtain_contract || 0);
  const sga      = Number(d.operating_sga || 0);
  const gp       = Number(d.gross_profit || 0);
  const gpm      = Number(d.gp_margin_pct || 0);
  const ebit     = Number(d.ebit || 0);
  const ebitPct  = Number(d.ebit_margin_pct || 0);
  const goldTotal = (gold.inventoryTotal || 0) + (gold.prepaidRemaining || 0);
  const navyTotal = cDirect + cAlloc + obtain + sga;

  document.getElementById('dash-content').innerHTML = `
    <div class="kpi-grid">
      <div class="kpi-tile green"><div class="kpi-lbl">Doanh thu ${d.year}</div><div class="kpi-val">${fmtCompact(doanhThu)} <span class="unit">VNĐ</span></div></div>
      <div class="kpi-tile ${gp>=0?'green':'red'}"><div class="kpi-lbl">Gross Profit</div><div class="kpi-val">${fmtCompact(gp)} <span class="unit">VNĐ</span></div><div class="kpi-hint">GPM: ${gpm}%</div></div>
      <div class="kpi-tile ${ebit>=0?'green':'red'}"><div class="kpi-lbl">EBIT</div><div class="kpi-val">${fmtCompact(ebit)} <span class="unit">VNĐ</span></div><div class="kpi-hint">${ebitPct}%</div></div>
      <div class="kpi-tile gold"><div class="kpi-lbl">Gold (chưa vào P&L)</div><div class="kpi-val">${fmtCompact(goldTotal)} <span class="unit">VNĐ</span></div><div class="kpi-hint">Inv + Prepaid</div></div>
    </div>

    <div class="panel">
      <div class="panel-hd"><h3>P&L Structure (IFRS 5 nhóm)</h3></div>
      <div class="pl-structure">
        <div class="pl-row pos"><span>Doanh thu</span><span class="money">${formatVND(doanhThu)}</span></div>
        <div class="pl-row neg"><span>(−) COGS Trực tiếp <small>(Contract_Direct)</small></span><span class="money">${formatVND(cDirect)}</span></div>
        <div class="pl-row neg"><span>(−) COGS Phân bổ <small>(Contract_Allocated)</small></span><span class="money">${formatVND(cAlloc)}</span></div>
        <div class="pl-row subtotal ${gp>=0?'pos':'neg'}"><span>= Gross Profit</span><span class="money">${formatVND(gp)} <small>(${gpm}%)</small></span></div>
        <div class="pl-row neg"><span>(−) CP Đấu thầu <small>(Obtain_Contract)</small></span><span class="money">${formatVND(obtain)}</span></div>
        <div class="pl-row neg"><span>(−) SG&A <small>(Operating_SGA)</small></span><span class="money">${formatVND(sga)}</span></div>
        <div class="pl-row total ${ebit>=0?'pos':'neg'}"><span>= EBIT</span><span class="money">${formatVND(ebit)} <small>(${ebitPct}%)</small></span></div>
      </div>
    </div>

    <div class="panel-grid-2">
      <div class="panel">
        <div class="panel-hd"><h3>Navy (đã vào P&L)</h3></div>
        <div style="position:relative;height:280px;width:100%"><canvas id="chart-navy"></canvas></div>
        <div class="panel-sub"><strong>${formatVND(navyTotal)} VNĐ</strong> <span class="muted">tổng đã ghi nhận CP</span></div>
      </div>
      <div class="panel">
        <div class="panel-hd"><h3>Gold (chưa vào P&L)</h3></div>
        <div class="gold-breakdown">
          <div class="gold-item"><div class="gold-lbl">Kho vật tư (Inventory)</div><div class="gold-val">${formatVND(gold.inventoryTotal)}</div></div>
          <div class="gold-item"><div class="gold-lbl">CP chờ phân bổ (Prepaid)</div><div class="gold-val">${formatVND(gold.prepaidRemaining)}</div></div>
          <div class="gold-item gold-total"><div class="gold-lbl">TỔNG GOLD</div><div class="gold-val">${formatVND(goldTotal)}</div></div>
        </div>
      </div>
    </div>
  `;

  renderNavyChart({ cDirect, cAlloc, obtain, sga });
}

function renderNavyChart(navy) {
  renderChart('chart-navy', {
    type: 'doughnut',
    data: {
      labels: ['COGS Direct', 'COGS Allocated', 'Obtain Contract', 'SG&A'],
      datasets: [{
        data: [navy.cDirect, navy.cAlloc, navy.obtain, navy.sga],
        backgroundColor: [PALETTE.navy, PALETTE.navyLight, PALETTE.orange, PALETTE.blue],
        borderWidth: 0
      }]
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: {
        legend: { position: 'right' },
        tooltip: { callbacks: { label: ctx => ctx.label + ': ' + fmtCompact(ctx.raw) + ' VNĐ' } }
      }
    }
  });
}
